import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/companies`, { withCredentials: true });
      setCompanies(r.data);
    } catch (err) {
      toast.error('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleDelete = async (companyId) => {
    if (!window.confirm('Deactivate this company? Members will lose corporate bypass.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/companies/${companyId}`, { withCredentials: true });
      toast.success('Company deactivated');
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  return (
    <div data-testid="admin-companies-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#0A0A0A]">Corporate Companies</h1>
          <p className="text-[#525252] text-sm mt-1">Approve domains and assign company admins</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full h-11 px-5" data-testid="add-company-button">
          <Plus className="w-4 h-4 mr-1" /> Approve domain
        </Button>
      </div>

      {loading ? (
        <p className="text-[#525252]">Loading…</p>
      ) : companies.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-[#0A0A0A]/10">
          <Building2 className="w-12 h-12 mx-auto text-[#525252] mb-3" />
          <p className="text-[#0A0A0A] font-medium mb-1">No companies yet</p>
          <p className="text-[#525252] text-sm">Approve your first corporate domain to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl overflow-hidden border border-[#0A0A0A]/10">
          <table className="w-full text-sm">
            <thead className="bg-[#EBE6DC]/50">
              <tr className="text-left text-[#525252]">
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Domain</th>
                <th className="px-4 py-3 font-medium">Admin</th>
                <th className="px-4 py-3 font-medium">Members</th>
                <th className="px-4 py-3 font-medium">Subscription</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.company_id} className="border-t border-[#0A0A0A]/10" data-testid={`company-row-${c.company_id}`}>
                  <td className="px-4 py-3 text-[#0A0A0A] font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-[#525252]">@{c.domain}</td>
                  <td className="px-4 py-3 text-[#525252]">
                    {c.admin_email ? (
                      <>
                        <div>{c.admin_name}</div>
                        <div className="text-xs text-[#a3a3a3]">{c.admin_email}</div>
                      </>
                    ) : (
                      <span className="text-[#B8935A] text-xs">Not assigned</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#0A0A0A]">{c.num_employees}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      c.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' :
                      c.subscription_status === 'expired' ? 'bg-red-500/20 text-red-400' :
                      'bg-gray-500/20 text-[#525252]'
                    }`}>
                      {c.subscription_status || 'inactive'}
                    </span>
                    {c.num_cards_paid > 0 && (
                      <span className="text-xs text-[#525252] ml-2">{c.num_cards_paid} cards</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${c.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-[#525252]'}`}>
                      {c.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditing(c)} className="border-[#0A0A0A]/15 text-[#525252] hover:bg-[#EBE6DC] h-8 px-3" data-testid={`edit-${c.company_id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      {c.is_active && (
                        <Button size="sm" variant="outline" onClick={() => handleDelete(c.company_id)} className="border-red-700 text-red-400 hover:bg-red-500/10 h-8 px-3" data-testid={`delete-${c.company_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && <AddCompanyModal onClose={() => setShowAdd(false)} onCreated={fetchCompanies} />}
      {editing && <EditCompanyModal company={editing} onClose={() => setEditing(null)} onUpdated={fetchCompanies} />}
    </div>
  );
};

const AddCompanyModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    domain: '',
    name: '',
    description: '',
    admin_email: '',
    allow_self_signup: true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.admin_email) delete payload.admin_email;
      if (!payload.description) delete payload.description;
      await axios.post(`${BACKEND_URL}/api/admin/companies`, payload, { withCredentials: true });
      toast.success('Company approved');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-[#0A0A0A]/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-company-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#0A0A0A]">Approve corporate domain</h2>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Domain *</Label>
            <Input
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              placeholder="acmeco.com"
              className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
              data-testid="modal-domain"
              required
            />
          </div>
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Company name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="AcmeCo"
              className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
              data-testid="modal-name"
              required
            />
          </div>
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl"
              data-testid="modal-description"
            />
          </div>
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Admin email (must exist)</Label>
            <Input
              type="email"
              value={form.admin_email}
              onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
              placeholder="admin@acmeco.com"
              className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11"
              data-testid="modal-admin-email"
            />
            <p className="text-xs text-[#a3a3a3] mt-1">Optional. User must have signed up. Can be set later.</p>
          </div>
          <div className="flex items-center justify-between bg-[#F5F1EA] rounded-xl p-3 border border-[#0A0A0A]/10">
            <Label className="text-[#0A0A0A] text-sm">Allow self-signup</Label>
            <button
              type="button"
              onClick={() => setForm({ ...form, allow_self_signup: !form.allow_self_signup })}
              className={`relative w-12 h-6 rounded-full transition-colors ${form.allow_self_signup ? 'bg-[#0A0A0A]' : 'bg-white'}`}
              data-testid="modal-self-signup"
            >
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.allow_self_signup ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1 border-[#0A0A0A]/15 text-[#525252] hover:bg-[#EBE6DC] rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full" data-testid="modal-save">
              {saving ? 'Saving…' : 'Approve'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditCompanyModal = ({ company, onClose, onUpdated }) => {
  const [form, setForm] = useState({
    name: company.name || '',
    description: company.description || '',
    admin_email: '',
    subscription_status: company.subscription_status || 'inactive',
    num_cards_paid: company.num_cards_paid || 0,
    allow_self_signup: company.allow_self_signup,
    is_active: company.is_active,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.admin_email) delete payload.admin_email;
      payload.num_cards_paid = Number(payload.num_cards_paid);
      await axios.put(`${BACKEND_URL}/api/admin/companies/${company.company_id}`, payload, { withCredentials: true });
      toast.success('Company updated');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full border border-[#0A0A0A]/10 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-company-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#0A0A0A]">Edit @{company.domain}</h2>
          <button onClick={onClose} className="text-[#525252] hover:text-[#0A0A0A]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Company name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11" />
          </div>
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl" />
          </div>
          <div>
            <Label className="text-[#0A0A0A] mb-2 block">Reassign admin (email)</Label>
            <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="new-admin@domain" className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11" data-testid="edit-admin-email" />
            <p className="text-xs text-[#a3a3a3] mt-1">Current: {company.admin_email || 'not assigned'}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#0A0A0A] mb-2 block">Subscription</Label>
              <select
                value={form.subscription_status}
                onChange={(e) => setForm({ ...form, subscription_status: e.target.value })}
                className="w-full bg-[#F5F1EA] border border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11 px-3"
                data-testid="edit-sub-status"
              >
                <option value="inactive">inactive</option>
                <option value="active">active</option>
                <option value="expired">expired</option>
              </select>
            </div>
            <div>
              <Label className="text-[#0A0A0A] mb-2 block">Cards paid</Label>
              <Input type="number" value={form.num_cards_paid} onChange={(e) => setForm({ ...form, num_cards_paid: e.target.value })} className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] rounded-xl h-11" data-testid="edit-num-cards" />
            </div>
          </div>
          <div className="flex items-center justify-between bg-[#F5F1EA] rounded-xl p-3 border border-[#0A0A0A]/10">
            <Label className="text-[#0A0A0A] text-sm">Allow self-signup</Label>
            <button type="button" onClick={() => setForm({ ...form, allow_self_signup: !form.allow_self_signup })} className={`relative w-12 h-6 rounded-full ${form.allow_self_signup ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.allow_self_signup ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between bg-[#F5F1EA] rounded-xl p-3 border border-[#0A0A0A]/10">
            <Label className="text-[#0A0A0A] text-sm">Active</Label>
            <button type="button" onClick={() => setForm({ ...form, is_active: !form.is_active })} className={`relative w-12 h-6 rounded-full ${form.is_active ? 'bg-green-500' : 'bg-white'}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${form.is_active ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1 border-[#0A0A0A]/15 text-[#525252] hover:bg-[#EBE6DC] rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white rounded-full" data-testid="edit-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCompanies;
